
import { useRouter } from 'next/router';
import { AreaPageListing, SingleAddress } from '@zoocasa/go-search';
import { AddressViewModel, AreaListingsPage, AreaListingsPageServerSideProps, AreaListingsPageViewModel, getAreaListingsPageServerSideProps } from 'components/pages/area-listings';
import HeadData from 'components/head-data';
import Layout from 'components/layout';
import { createAddressesApi, parsedAddressPathFromJson } from 'data/addresses';
import { createInsightsApi } from 'data/insights';
import { createNamedContentsApi } from 'data/named-content';
import { createSearchApi } from 'data/search';
import { createSeoLinksApi } from 'data/seo-links';
import { DynamicPageServerSideProps } from 'types';
import {
  createGetServerSideProps,
  HandlerType,
} from 'utils/next/shared-getServerSideProps';
import { useFeaturesContext } from 'contexts/features';
import { HybridPage, HybridPageViewModel } from 'components/pages/hybrid';
import { getHybridPageServerSideProps, HybridPageServerSideProps } from 'components/pages/hybrid';
import { AreaPageV2 } from 'components/pages/area-page-v2';
import { useIsMobile } from 'hooks/use-size-class';
import { useState } from 'react';
import { getTenantById } from 'tenants';
import { AllowedExperimentKey } from '@tenants/types';
import { useExperimentVariation } from 'ui/experimental-section/hooks';
import { Namespaces } from 'types/locales';

const handler: HandlerType<serverSidePropsType> = async (context, features, tenant, i18n) => {
  const searchApi = createSearchApi();
  const addressesApi = createAddressesApi();
  const namedContentsApi = createNamedContentsApi();
  const insightsApi = createInsightsApi();
  const seoLinksApi = createSeoLinksApi(insightsApi);

  const shouldShowHybridAreaMapPage = features.showHybridAreaMapPage && tenant.features.ShowHybridAreaMapPage;
  if (shouldShowHybridAreaMapPage) {
    return await getHybridPageServerSideProps(context, features, tenant, searchApi, addressesApi, namedContentsApi, insightsApi, seoLinksApi, i18n);
  } 
  const props = await getAreaListingsPageServerSideProps(context, features, tenant, searchApi, addressesApi, namedContentsApi, insightsApi, seoLinksApi, i18n);
  return props;
};
export const getServerSideProps = createGetServerSideProps(handler, [Namespaces.HOME]);

type serverSidePropsType = DynamicPageServerSideProps<AreaListingsPageServerSideProps> | DynamicPageServerSideProps<HybridPageServerSideProps>;

export default function AreaPage({ routeName, props }: serverSidePropsType) {
  const { features } = useFeaturesContext();
  const isMobile = useIsMobile();
  const router = useRouter();
  const tenant = getTenantById(props.tenantId);
  const showHybridAreaMapPage = features.showHybridAreaMapPage && (props as HybridPageServerSideProps).showHybridAreaMapPage;
  // AreaPageV2 gate. Any of these enabling sources wins:
  // - `UseAreaPageV2` tenant flag (manual force-on; Zoocasa, eXp US, eXp CA)
  // - `x-zoocasa-features: useAreaPageV2=true` ModHeader override
  // - GrowthBook `dj-area-page-redesign` experiment variation
  const { showVariation: isAreaPageV2Experiment } = useExperimentVariation(AllowedExperimentKey.AREA_PAGE_V2_REDESIGN);
  const useAreaPageV2 = showHybridAreaMapPage
    && (
      (tenant?.features?.UseAreaPageV2 ?? false)
      || Boolean(features.useAreaPageV2)
      || isAreaPageV2Experiment
    );
  const useLayoutV2 = showHybridAreaMapPage && (tenant?.features?.UseNewHybridPageDesign ?? false);
  const [isMobileListView, setIsMobileListView] = useState(!useLayoutV2);
  const {
    requestedAddress: requestedAddressData,
    electedAddress: electedAddressData,
    listings: listingsData,
    ...viewModelData
  } = props as AreaListingsPageViewModel<AddressViewModel> | HybridPageViewModel<AddressViewModel>;

  const requestedAddress = parsedAddressPathFromJson(requestedAddressData);
  const electedAddress = SingleAddress.fromJSON(electedAddressData);
  /** We need to convert the listings data from a plain object back into an AreaPageListing Model */
  const listings = listingsData.map((listing: unknown) => AreaPageListing.fromJSON(listing));

  const viewModel: AreaListingsPageViewModel<AddressViewModel> = {
    requestedAddress,
    electedAddress,
    listings,
    ...viewModelData,
  };

  // Use router.asPath as key to force component remount when URL changes (including query params)
  // This ensures the page updates when filters change
  // We apply the key to the page content, not the Layout, to avoid remounting header/footer
  // Only use asPath when router is ready to avoid SSR/client mismatch (https://nextjs.org/docs/pages/api-reference/functions/use-router#router-object)
  const pageKey = router.isReady ? router.asPath : router.pathname;

  if (showHybridAreaMapPage) {
    if (useAreaPageV2) {
      return (
        <Layout
          routeName={routeName}
          showFooter={false}
          showMobileBottomNav={false}
          renderHeader={() => null}
        >
          {props.headTags && <HeadData data={props.headTags} />}
          <AreaPageV2 key={pageKey} viewModel={viewModel} tenantId={props.tenantId} />
        </Layout>
      );
    }
    return (
      <Layout routeName={routeName} showFooter={!isMobile || (isMobile && isMobileListView)}>
        {props.headTags && <HeadData data={props.headTags} />}
        <HybridPage key={pageKey} viewModel={viewModel} isMobileListView={isMobileListView} setIsMobileListView={setIsMobileListView} tenantId={props.tenantId} />
      </Layout>
    );
  }
  return (
    <Layout routeName={routeName}>
      {props.headTags && <HeadData data={props.headTags} />}
      <AreaListingsPage key={pageKey} viewModel={viewModel}/>
    </Layout>
  );
}